import React, { useMemo } from "react";
import { useGetPagesQuery } from "../../features/pages/pageApi";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import {
  useAddRoleMutation,
  useDeleteRoleMutation,
  useGetRolesQuery,
  useUpdateRoleMutation,
} from "../../features/roles/roleApi";
import MasterCrudPage from "../masters/MasterCrudPage";
import { booleanFilterOptions, withAuditFilters } from "../masters/filterMetadata";
import { buildNameCodeIdLabel, getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const AccessBadge = ({ enabled }) => (
  <span
    className={`inline-flex min-w-12 justify-center rounded-full border px-2 py-1 text-xs font-medium ${
      enabled
        ? "border-green-200 bg-green-50 text-green-700"
        : "border-gray-200 bg-gray-50 text-gray-500"
    }`}
  >
    {enabled ? "Yes" : "No"}
  </span>
);

const roleFilterFields = withAuditFilters([
  { name: "role_id", label: "Role ID", optionSource: "roles" },
  { name: "role_name", label: "Role Name" },
  {
    name: "can_view",
    label: "Can View",
    type: "select",
    options: booleanFilterOptions,
  },
  {
    name: "can_create",
    label: "Can Create",
    type: "select",
    options: booleanFilterOptions,
  },
  {
    name: "can_update",
    label: "Can Update",
    type: "select",
    options: booleanFilterOptions,
  },
  {
    name: "can_delete",
    label: "Can Delete",
    type: "select",
    options: booleanFilterOptions,
  },
]);

const Role = () => {
  const { data: pageData, isFetching: isLoadingPages, isError: pageLoadFailed, refetch: reloadPages } =
    useGetPagesQuery({ page: 1, limit: 1000 });
  const pageOptions = useMemo(() => (pageData?.data || []).map((page) => ({
    value: page.page_id,
    label: buildNameCodeIdLabel({ name: page.page_name, id: page.page_id }),
  })), [pageData?.data]);
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
      },
      {
        name: "role_name",
        label: "Role Name",
        placeholder: "Enter role name",
        required: true,
        fullWidth: true,
      },
      {
        name: "page_access",
        label: "Page Access",
        type: "select",
        multiple: true,
        options: pageOptions,
        placeholder: isLoadingPages ? "Loading pages..." : pageLoadFailed ? "Unable to load pages. Reopen the form to retry." : "Select pages",
        disabled: (isLoadingPages || pageLoadFailed) && pageOptions.length === 0,
        fullWidth: true,
      },
      { name: "can_view", label: "Can View", type: "checkbox" },
      { name: "can_create", label: "Can Create", type: "checkbox" },
      { name: "can_update", label: "Can Update", type: "checkbox" },
      { name: "can_delete", label: "Can Delete", type: "checkbox" },
    ],
    [isLoadingPlants, plantOptions, isLoadingPages, pageLoadFailed, pageOptions]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "Role ID",
        selector: (row) => row?.role_id || "-",
        sortable: true,
      },
      {
        name: "Role Name",
        selector: (row) => row?.role_name || "-",
        sortable: true,
        grow: 2,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
      {
        name: "Page Access",
        selector: (row) => (row?.page_access || []).map((id) =>
          pageOptions.find((option) => option.value === id)?.label || id
        ).join(", ") || "-",
        grow: 2,
        wrap: true,
      },
      {
        name: "View",
        selector: (row) => (row?.can_view ? "Yes" : "No"),
        cell: (row) => <AccessBadge enabled={row?.can_view} />,
      },
      {
        name: "Create",
        selector: (row) => (row?.can_create ? "Yes" : "No"),
        cell: (row) => <AccessBadge enabled={row?.can_create} />,
      },
      {
        name: "Update",
        selector: (row) => (row?.can_update ? "Yes" : "No"),
        cell: (row) => <AccessBadge enabled={row?.can_update} />,
      },
      {
        name: "Delete",
        selector: (row) => (row?.can_delete ? "Yes" : "No"),
        cell: (row) => <AccessBadge enabled={row?.can_delete} />,
      },
    ],
    [pageOptions]
  );

  return (
    <MasterCrudPage
      entityName="Role"
      pageTitle="Roles"
      idField="role_id"
      displayField="role_name"
      fields={fields}
      columns={columns}
      searchFields={[
        "role_id",
        "role_name",
        "page_access",
        (row) => (row?.page_access || []).map((id) => pageOptions.find((option) => option.value === id)?.label || id).join(" "),
        "plant_id",
        (row, metaData) => getPlantLabel(row, metaData),
        "can_view",
        "can_create",
        "can_update",
        "can_delete",
      ]}
      searchPlaceholder="Search roles"
      useListQuery={useGetRolesQuery}
      useCreateMutation={useAddRoleMutation}
      useUpdateMutation={useUpdateRoleMutation}
      useDeleteMutation={useDeleteRoleMutation}
      filterFields={roleFilterFields}
      transformPayload={(payload, editData) => {
        if (!editData) return payload;
        const updatePayload = { ...payload };
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={() => {
        loadPlantOptions();
        reloadPages();
      }}
      noDataLabel="No roles found"
    />
  );
};

export default Role;
